import {
  forwardRef,
  ErrorInfo,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Heading } from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { ErrorBoundary } from 'react-error-boundary';

// TODOs: this is present in v3 branch
// import '../assets/styles.css';

import '../translations/i18n';

import {
  CardFieldKeyType,
  ContactFieldKeyType,
  BillingFieldKeyType,
  PaymentCardFormProps,
  FormConfigType,
} from '../@types';
import { useGateway, useDynamicFormFieldRefs } from '../hooks';
import { BillingFields } from '../constants';
import { useGlobalStore } from '../store/useGlobalStore';
import { getCardBrandTokenexName } from '../utils/getCardBrandTokenexName';
import { useCreditCardFormStore } from '../hooks/useCreditCardFormStore';
import { createLogLocal } from '../utils/createLogLocal';

import { CardReaderWrapper } from './CardReaderWrapper';
import { GatewayContainer } from './GatewayContainer';
import { CSSResetPT } from './CSSResetPT';
import { FailedToLoad } from './ui/FailedToLoad';
import { RenderComponent } from './RenderComponent';
import { CheckboxControl } from './ui/controls/CheckboxControl';
import { Loading } from './ui/Loading';

type FormInputsType = Record<string, any>;

const DefaultFormConfig: FormConfigType = {
  cardFields: {},
  contactFields: {},
  billingFields: {},
};
const log = createLogLocal('PaymentCardForm');

const logBoundaryError = (error: Error, info: ErrorInfo) => {
  log(
    'There is an error in a component! Check boundary error log for details.',
  );
  window.console.error(error);
  window.console.error(info);
};

/** If an error has one of these types, it is considered a validation error */
const validationTypes = [
  'validation',
  'required',
  'nativeValidation',
  'cardNumber',
  'valid',
  'validate',
  'format',
  'customRegEx',
];

/**
 * Separate form errors into validation errors and regular errors, based on error type.
 */
const separateErrors = (formErrors: Record<string, any>) => {
  const errors: Record<string, any> = {};
  const validationErrors: Record<string, any> = {};

  Object.entries(formErrors).forEach(([errName, errObj]: [string, any]) => {
    // Handle validation/required errors separately
    if (validationTypes.includes(errObj.type)) {
      validationErrors[errName] = errObj.message;
    } else {
      errors[errName] = errObj.message;
    }
  });

  return { errors, validationErrors };
};

/**
 * Processes form errors and dispatches them to appropriate handlers.
 */
const handleFormErrors = (
  formStateErrors: Record<string, any> | undefined,
  onErrorHandler?: (errors: any) => void,
  onValidationErrorHandler?: (errors: any) => void,
  context?: 'validate' | 'submit',
) => {
  if (!formStateErrors || Object.keys(formStateErrors).length === 0) return;

  const { errors, validationErrors } = separateErrors(formStateErrors);

  const ctx = context ?? 'handleFormErrors';
  log(ctx, 'errors:', errors, 'validationErrors:', validationErrors);

  // Handle regular errors
  if (onErrorHandler && Object.keys(errors).length > 0) {
    onErrorHandler(errors);
  }

  // Handle validation errors
  if (onValidationErrorHandler && Object.keys(validationErrors).length > 0) {
    onValidationErrorHandler(validationErrors);
  }
};

/**
 * Apply to the topmost element of the payment card form.
 * Allows this component to be styled independently of the host application
 * if `scopedCss` is set to `true`.
 */
const paytokScopeClass = 'PaymentCardForm';

/**
 * @description Payment card form component for collecting and validating card, contact, and billing information.
 * Integrates with Stripe/Tokenex and supports dynamic field configuration, translations, and error boundaries.
 *
 * @param {PaymentCardFormProps} props - Props for configuring the form and handlers.
 * @param {any} fRef - Forwarded ref for imperative form actions.
 * @returns {JSX.Element} The payment card form UI.
 */

export const PaymentCardForm = forwardRef(
  (
    {
      env = 'prod',
      apiToken = '',
      propertyId,
      fields,
      lang,
      labels,
      canEditGuest = false,
      defaultValues = {},
      validateOnBlur = false,
      validateOnChange = false,
      scopedCss = true,
      // TODO: consider removing submit callbacks since the form returns a value now
      onSubmitHandler,
      onChangeHandler,
      onErrorHandler,
      onValidationErrorHandler,
      onValidateHandler,
      onTokenizeHandler,
      onLoadStart,
      onLoadEnd,
      cardBrands = null,
      mode = 'live',
    }: PaymentCardFormProps,
    fRef: any,
  ) => {
    useGlobalStore.getState().initialize({
      env,
      apiKey: apiToken,
      propertyId,
      lang,
    });

    const [sameAddress, setSameAddress] = useState(false);
    const { defineRef, refMap } = useDynamicFormFieldRefs<any>();

    const { t, i18n } = useTranslation();
    const { cardFields, contactFields, billingFields } = {
      ...DefaultFormConfig,
      ...(fields || {}),
    };

    const {
      loaded: gatewayLoaded,
      options: gatewayOptions,
      provider: gatewayProvider,
      error: gatewayError,
      fieldMap: FormFieldMap,
      focusSecureInput,
      setCardNumber,
    } = useGateway({
      propertyId,
      env,
      apiToken,
      mode,
    });

    const providerName = gatewayProvider === 'tokenex' ? 'tokenex' : 'stripe';

    const { setCardBrands } = useCreditCardFormStore();
    const { cardBrands: cardBrandsStored } = useCreditCardFormStore();

    /*
     * [EFFECT]
     * Handle setting card brands from prop change.
     */
    useEffect(() => {
      // Only store allowed card brands once (they don't change after load)
      if (cardBrandsStored.length > 0) return;

      // Store card brands if not stored yet.
      if (cardBrands && cardBrands.length > 0) {
        const brands = cardBrands.map(brand => getCardBrandTokenexName(brand));
        setCardBrands(brands);
      }
    }, [cardBrands, cardBrandsStored.length, setCardBrands]);

    log('cardBrandsStored', cardBrandsStored, 'cardBrands', cardBrands);

    /**
     * Set up default form values, excluding the billing fields.
     * Uses default values from props.
     */
    const defaultValuesWithoutBilling = useMemo(
      () =>
        Object.fromEntries(
          Object.entries(defaultValues).filter(
            el => !BillingFields.includes(el[0] as BillingFieldKeyType),
          ),
        ),
      [defaultValues],
    );

    const formMethods = useForm<FormInputsType>({
      mode: validateOnBlur
        ? 'onBlur'
        : validateOnChange
          ? 'onChange'
          : 'onSubmit',
      defaultValues: defaultValuesWithoutBilling,
    });

    const hasBillingFields = useMemo(
      () => Object.keys(billingFields).length > 0,
      [billingFields],
    );
    const hasContactFields = useMemo(
      () => Object.keys(contactFields).length > 0,
      [contactFields],
    );
    const hasCardFields = useMemo(
      () => Object.keys(cardFields).length > 0,
      [cardFields],
    );
    const hasSameAddress = useMemo(
      () =>
        Object.keys(defaultValues).filter(d => d.startsWith('billing')).length >
        0,
      [defaultValues],
    );

    useEffect(() => {
      if (!gatewayLoaded) onLoadStart?.();
    }, [onLoadStart, gatewayLoaded]);

    useEffect(() => {
      if (gatewayLoaded) onLoadEnd?.();
    }, [onLoadEnd, gatewayLoaded]);

    useLayoutEffect(() => {
      if (labels) {
        const langs = Object.keys(labels);
        langs.forEach(l => {
          i18n.addResourceBundle(l, 'translation', labels[l]);
        });
      }
    }, [i18n, labels]);

    useLayoutEffect(() => {
      i18n.changeLanguage(lang);
    }, [i18n, lang]);

    /**
     * @description Validates the form and all dynamic field refs.
     * Calls error and validate handlers as appropriate.
     * @returns {Promise<boolean>} True if the form is valid.
     */
    const validate = async () => {
      const isFormValid = await formMethods.trigger();

      const responses: any[] = [];
      refMap.forEach((componentRef: any) => {
        const validateFunc = componentRef.current?.validate;
        if (validateFunc) responses.push(validateFunc());
      });

      const isValid = (await Promise.all(responses))
        .filter(el => el !== undefined)
        .every(Boolean);

      log(
        '[@cloudbeds/payment-tokenization] Form validation state:',
        isValid && isFormValid,
        {
          ...formMethods.formState.errors,
        },
      );

      // Handle errors if validation failed
      if (!isValid || !isFormValid) {
        handleFormErrors(
          formMethods?.formState?.errors,
          onErrorHandler,
          onValidationErrorHandler,
          'validate',
        );
      }

      if (onValidateHandler) {
        onValidateHandler({
          valid: isValid && isFormValid,
          ...formMethods?.formState?.errors,
        });
      }

      return isValid && isFormValid;
    };

    /**
     * @description Returns the current form state from react-hook-form.
     * @returns {FormState<FormInputsType>} The form state.
     */
    const getFormState = () => formMethods?.formState;
    log('getFormState()', getFormState());

    /**
     * @description Submits the form after running beforeSubmit on all refs.
     * Handles tokenization and calls submit/tokenize handlers.
     * @returns {Promise<any>} The submitted form values or empty object.
     */
    const submit = async () => {
      const responses: any[] = [];
      refMap.forEach((componentRef: any) => {
        const beforeSubmit = componentRef.current?.beforeSubmit;
        if (beforeSubmit) responses.push(beforeSubmit());
      });

      const isTokenized = (await Promise.all(responses))
        .filter(el => el !== undefined)
        .every(Boolean);
      if (isTokenized) {
        formMethods.handleSubmit(
          async (formData: any) => {
            const isValid = await formMethods.trigger();
            if (!isValid) return;

            onSubmitHandler({ provider: providerName, ...formData });
            if (onTokenizeHandler)
              onTokenizeHandler({ provider: providerName, ...formData });
          },
          async () => {
            handleFormErrors(
              formMethods?.formState?.errors,
              onErrorHandler,
              onValidationErrorHandler,
              'submit',
            );
          },
        )();
        // TODO: check this - returned values probably different from the data passed to callbacks.
        const values = formMethods.getValues();
        return { provider: providerName, ...values };
      }

      return {};
    };

    /**
     * @description Handles toggling the "same as address" checkbox for billing fields.
     * @param {any} isChecked - Whether the checkbox is checked.
     */
    const onSameAddressChange = (isChecked: any) => {
      Object.entries(defaultValues).forEach(([key, val]) => {
        if (BillingFields.includes(key as BillingFieldKeyType)) {
          if ((val || '').trim() !== '') {
            formMethods.setValue(key, isChecked ? val : '');
          }
        }
      });
      setSameAddress(isChecked);
    };

    useImperativeHandle(fRef, () => ({
      submit,
      validate,
      getFormState,
    }));

    if (gatewayError) {
      return <FailedToLoad />;
    }
    if (!gatewayLoaded || FormFieldMap === null) {
      return <Loading />;
    }

    return (
      <ErrorBoundary
        FallbackComponent={FailedToLoad}
        onError={logBoundaryError}
      >
        <CardReaderWrapper
          formMethods={formMethods}
          setCardNumber={setCardNumber}
          focusSecureInput={focusSecureInput}
        />
        <CSSResetPT scope={scopedCss ? `.${paytokScopeClass}` : ''} />
        <Box className={paytokScopeClass}>
          <GatewayContainer provider={gatewayProvider} options={gatewayOptions}>
            <FormProvider {...formMethods}>
              {/**
               * ********** CONTACT FIELDS SECTION **********
               * Contains fields for: firstName, lastName, fullName
               */}
              {hasContactFields && (
                <Box className="ContactFieldsSection FieldsSection" mb={4}>
                  {FormFieldMap.contactFields.meta?.label &&
                  t(FormFieldMap.contactFields.meta?.label) ? (
                    <Heading className="FieldsSectionHeading">
                      {t(FormFieldMap.contactFields.meta?.label)}
                    </Heading>
                  ) : null}
                  <Box className="FieldsSectionBody">
                    {Object.keys(contactFields).map(el => (
                      <RenderComponent
                        key={`key-contactFields-${el}`}
                        field={
                          FormFieldMap.contactFields.fields[
                            el as ContactFieldKeyType
                          ]
                        }
                        config={contactFields[el as ContactFieldKeyType]}
                        ref={defineRef(`contactField-${el}`)}
                        onErrorHandler={onErrorHandler}
                        onChangeHandler={onChangeHandler}
                        validateOnBlur={validateOnBlur}
                        validateOnChange={validateOnChange}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/**
               * ********** CARD FIELDS SECTION **********
               * Contains fields for: cardNumber, cardExpire, cardholderName
               */}
              {hasCardFields && (
                <Box className="CardFieldsSection FieldsSection" mb={4}>
                  {FormFieldMap.cardFields.meta?.label &&
                  t(FormFieldMap.cardFields.meta?.label) ? (
                    <Heading className="FieldsSectionHeading">
                      {t(FormFieldMap.cardFields.meta?.label)}
                    </Heading>
                  ) : null}
                  <Box className="FieldsSectionBody">
                    {/* Where the card fields actually render */}
                    {/* ====================== @TODO PUT DEFAULT VALUES IN HERE ====================== */}
                    {Object.keys(cardFields).map(el => (
                      <RenderComponent
                        key={`key-cardFields-${el}`}
                        field={
                          FormFieldMap.cardFields.fields[el as CardFieldKeyType]
                        }
                        config={cardFields[el as CardFieldKeyType]}
                        ref={defineRef(`cardField-${el}`)}
                        onErrorHandler={onErrorHandler}
                        onChangeHandler={onChangeHandler}
                        validateOnBlur={validateOnBlur}
                        validateOnChange={validateOnChange}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/**
               * ********** BILLING FIELDS SECTION **********
               * Fields for: billingCountry, billingZipe, billingEmail, etc.
               */}
              {hasBillingFields && (
                <Box className="BillingFieldsSection FieldsSection" mb={4}>
                  {FormFieldMap.billingFields.meta?.label &&
                  t(FormFieldMap.billingFields.meta?.label) ? (
                    <Heading className="FieldsSectionHeading">
                      {t(FormFieldMap.billingFields.meta?.label)}
                    </Heading>
                  ) : null}

                  <Box className="FieldsSectionBody">
                    {hasSameAddress && (
                      <CheckboxControl
                        id="sameAddress"
                        name="sameAddress"
                        label={t('sameAsAddress')}
                        sx={{
                          px: 0,
                          flexBasis: '100%',
                        }}
                        onChangeHandler={onSameAddressChange}
                      />
                    )}
                    {Object.keys(billingFields).map(el => (
                      <RenderComponent
                        key={`key-billingFields-${el}`}
                        field={
                          FormFieldMap.billingFields.fields[
                            el as BillingFieldKeyType
                          ]
                        }
                        config={billingFields[el as BillingFieldKeyType]}
                        isDisabled={
                          !canEditGuest &&
                          sameAddress &&
                          defaultValues[el as BillingFieldKeyType] &&
                          (
                            defaultValues[el as BillingFieldKeyType] || ''
                          ).trim() !== '' &&
                          defaultValues[el as BillingFieldKeyType] ===
                            formMethods.getValues(el)
                        }
                        ref={defineRef(`billingField-${el}`)}
                        onErrorHandler={onErrorHandler}
                        onChangeHandler={onChangeHandler}
                        validateOnBlur={validateOnBlur}
                        validateOnChange={validateOnChange}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </FormProvider>
          </GatewayContainer>
        </Box>
      </ErrorBoundary>
    );
  },
);
